import { Form, Formik, FormikValues } from 'formik';
import React from 'react';
import { ConsentsCheckBox, ImageUpload, PhoneNumberInput } from '../../components';
import { Button, ProgressCircular, useTheme } from '@sky-distribution/react-commons-ui';
import {
  CardWrapper,
  FormWrapper,
  ProgressCircularStyle,
  RequiredText,
  SubmitButtonWrapper,
  StyledTitleH1,
  TuwDataProcessingText
} from './Form.styled';
import { InfoCard } from '../../components/InfoCard';
import { useNavigate } from 'react-router-dom';
import { useTypedTranslate } from '../../translations/useTypedTranslate';
import { useAssistanceFormContext } from '../../common/contexts/AssistanceForm/useAssistanceFormContext';
import { TENANT_NAME, Tenant } from '../../config/tenants';
import { useIsTenant } from '../../config/utils/useIsTenant';
import { useConfigValuesContext } from '../../common/contexts/ConfigValues/useConfigValuesContext';
import { StyledLinkedText } from '../../components/Common';

const REQUIRED = 'Required';
const FILE_TOO_LARGE = 'FILE_TOO_LARGE';

type ErrorMessages = {
  file?: string;
  phoneNumber?: string;
  termsAgreed?: string;
};

const validate = (values: FormikValues): ErrorMessages => {
  const errors: ErrorMessages = {};

  if (values.fileSizeExceeded) {
    errors.file = FILE_TOO_LARGE;
  } else if (!values.file) {
    errors.file = REQUIRED;
  }

  const requiredFields = TENANT_NAME === Tenant.TUW ? ['phoneNumber'] : ['phoneNumber', 'termsAgreed'];
  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = REQUIRED;
    }
  });

  return errors;
};

export const FormPage = (): JSX.Element => {
  const { t } = useTypedTranslate();
  const theme = useTheme();
  const navigate = useNavigate();
  const { submitAssistanceForm, cachedFormData } = useAssistanceFormContext();
  const { configValues } = useConfigValuesContext();
  const isTuw = useIsTenant(Tenant.TUW);

  const handleCheckboxChange = (value: boolean, setFieldValue: (name: string, value: boolean) => void): void => {
    setFieldValue('termsAgreed', value);
  };

  const handleSubmit = async (values: FormikValues, setSubmitting: (isSubmitting: boolean) => void): Promise<void> => {
    setSubmitting(true);
    document.body.style.overflow = 'hidden';

    const redirectPath = await submitAssistanceForm(values);
    setSubmitting(false);
    document.body.style.overflow = 'auto';
    navigate(redirectPath);
  };

  return (
    <FormWrapper data-testid="form-page">
      <StyledTitleH1>{t('FORM_TITLE')}</StyledTitleH1>
      <CardWrapper shadow={true} radius={2}>
        <FormWrapper data-testid="formik-wrapper">
          <Formik
            validate={validate}
            initialValues={{
              phoneNumber: cachedFormData.phoneNumber,
              file: '',
              fileSizeExceeded: false,
              termsAgreed: isTuw,
              latitude: '0',
              longitude: '0',
            }}
            onSubmit={(values, { setSubmitting }) => handleSubmit(values, setSubmitting)}
          >
            {({ setFieldValue, values, errors, isSubmitting, touched, submitCount }) => {
              const phoneNumberInput = (
                <PhoneNumberInput
                  setFieldValue={setFieldValue}
                  error={touched.phoneNumber && errors.phoneNumber ? (errors.phoneNumber as string) : undefined}
                  initialValue={cachedFormData.phoneNumber}
                />
              );

              return (
                <Form>
                  <InfoCard />
                  {!isTuw ? phoneNumberInput : null}
                  <ImageUpload setFieldValue={setFieldValue} />
                  {errors.file === FILE_TOO_LARGE ? (
                    <RequiredText variant="h4">{t('FILE_TOO_LARGE')}</RequiredText>
                  ) : errors.file === REQUIRED && values.file === undefined && submitCount > 0 ? (
                    <RequiredText variant="h4">{t('REQUIRED')}</RequiredText>
                  ) : null}
                  {isTuw ? phoneNumberInput : null}
                  <div style={{ margin: '1rem 0' }}>
                    {isTuw ? (
                      <TuwDataProcessingText variant="h4">
                        {t('CONSENT_TO_CONTACT_AND_READ_INFO')}{' '}
                        <StyledLinkedText href={configValues[TENANT_NAME].clause} target="_blank" rel="noopener">
                          {t('INFORMATION_REGARDING_DATA_PROCESSING')}
                        </StyledLinkedText>
                      </TuwDataProcessingText>
                    ) : (
                      <>
                        <ConsentsCheckBox
                          required={Boolean(errors.termsAgreed) && Boolean(touched.termsAgreed)}
                          id="termsAgreed"
                          checked={values.termsAgreed}
                          onChange={(value) => handleCheckboxChange(value, setFieldValue)}
                        />
                        {errors.termsAgreed && touched.termsAgreed ? (
                          <RequiredText variant="h4">{t('REQUIRED')}</RequiredText>
                        ) : null}
                      </>
                    )}
                  </div>
                  <SubmitButtonWrapper>
                    <Button
                      blockMode={!isTuw}
                      type="submit"
                      style={isTuw ? { color: theme.palette.button.text.primary.base } : undefined}
                    >
                      {t('NEXT')}
                    </Button>
                  </SubmitButtonWrapper>
                  {isSubmitting && (
                    <ProgressCircularStyle>
                      <ProgressCircular typeStyle="primary" />
                    </ProgressCircularStyle>
                  )}
                </Form>
              );
            }}
          </Formik>
        </FormWrapper>
      </CardWrapper>
    </FormWrapper>
  );
};
